import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Button, Form, Input, Modal } from 'antd';
import { createMuiTheme, MuiThemeProvider } from "@material-ui/core";
import Axios from "axios";
import MaterialTable from "material-table";
import moment from "moment-timezone";
import dividePages from "./../../../../util/dividePages";
import { useKeycloakUsersFormContext } from '../../../../util/keycloakUsersFormContext';

import {
	addUsersToKeycloak,
	updateUsersToKeycloak,
	deleteUsersKeycloak,
	getClientRoles,
  activateKeycloakUser,
	deactivateKeycloakUser,
	setRolesToUsers,
} from "../../../../util/KeycloakAPIUtil";
import { useRouter } from "../../../../util/router";
import { useUser, getPassword } from "../../../../util/user";
import GYAxios from "../../../../util/GYAxios";
import Noti from "../../../../util/noti";
import { sendUserExportedEmail, sendUserExportedNotificationToIT } from '../../../../util/email';
import TextArea from "antd/lib/input/TextArea";

const KeycloakPanelTable = (props) => {
	const router = useRouter();
	const loggedInUser = useUser();
	const [KCUsers, setKCUsers] = useState([]);
	const [KCRoles, setKCRoles] = useState([]);
	const [multiSites, setMultiSites] = useState([]);
	const [noOfPages, setNoOfPages] = useState([1]);
	const [tableState, setTableState] = useState(false); // Table is ready or not
	const [ emailNotiSubject, setEmailNotiSubject ] = useState('Your account on GYMonitoring website will be de-activated. Please login to activate your account.');
	const [ emailNotiBody, setEmailNotiBody ] = useState(
`<p>We noticed that you haven't logged in for a long time. Please log in within 2 weeks to not get your account de-activated.</p>
<p>You can login to our GY Monitoring website at this link: <a href="https://gymonitoring.com/solar/signin">https://gymonitoring.com/solar/signin</a></p>`
	);
	const [ usersToSendEmailNoti, setUsersToSendEmailNoti ] = useState(null);
	const [ isEmailNotificationModelOpen, setIsEmailNotificationModelOpen ] = useState(false);
	const { setSelectedKeycloakUsers } = useKeycloakUsersFormContext();

	const setReload = props.setReload;

	const width = 250;

	/**
	 * Format Sites row
	 * @param {obj} rowData
	 * @returns formatted row
	 */
	const renderSitesRows = (rowData) =>
		rowData.site_desc !== "All Sites" ? (
			rowData.site_desc === "Multiple Sites" ? (
				<b>{rowData.site_desc} *</b>
			) : (
				<b>{rowData.site_desc}</b>
			)
		) : (
			rowData.site_desc
		);

	const initColumns = [
		{ title: "Email", width: 310, field: "email", editable: "never" },
		{
			title: "Username",
			width,
			field: "username",
			editable: "never",
		},
		{
			title: "Sites",
			width: 270,
			grouping: false,
			field: "site_desc",
			render: renderSitesRows,
			editable: "never",
		},

		{
			title: "First Name",
			width,
			field: "first_name",
			editable: props.isEditable ? "always" : "never",
		},
		{
			title: "Last Name",
			width,
			field: "last_name",
			editable: props.isEditable ? "always" : "never",
		},
		{
			title: "Type",
			width,
			field: "type",
			lookup: { 1: "GY", 2: "Client" },
			editable: "never",
		},
		{
			title: "Company",
			width,
			field: "company",
			editable: "never",
		},
		{
			title: "Department",
			width: 300,
			grouping: false,
			field: "dept_desc",
			editable: "never",
		},
		{
			title: "Position",
			width: 310,
			grouping: false,
			field: "position",
			editable: props.isEditable ? "always" : "never",
		},
		{
			title: "Country",
			width: 150,
			grouping: true,
			field: "country",
			lookup: { TH: "Thailand", VN: "Vietnam", all: "-" },
			editable: props.isEditable ? "always" : "never",
		},
		{
			title: "See Invoices",
			width: 150,
			grouping: false,
			field: "see_invoice",
			lookup: { 1: "YES", 0: "NO" },
			editable: props.isEditable ? "always" : "never",
		},
		{
			title: "See Reports",
			width: 150,
			grouping: false,
			field: "see_report",
			lookup: { 1: "YES", 0: "NO" },
			editable: props.isEditable ? "always" : "never",
		},
		{
			title: "Receiving e-invoice",
			width: 100,
			grouping: false,
			field: "receiving_einvoice",
			lookup: { 1: "YES", 0: "NO", 2: "N/A" },
		},
		{
			title: "External User",
			width: 100,
			grouping: false,
			field: "is_external",
			lookup: { 1: "YES", 0: "NO" },
		},
		{
			title: "Last Logged-in",
			width,
			grouping: true,
			field: "last_connection",
			editable: "never",
		},
		{
			title: "Activated",
			width: 100,
			grouping: false,
			field: "activated_status",
			lookup: { 1: "YES", 0: "NO" },
			editable: "never",
		},
		{
			title: "Exported",
			width: 100,
			grouping: false,
			field: "exported_status",
			lookup: { 1: "YES", 0: "NO" },
			editable: "never",
		},
	];

	const theme = createMuiTheme({
		palette: {
			primary: {
				main: "#ff9800",
			},
			secondary: {
				main: "#88b917",
			},
		},
	});

	/**
	 * Fetch client roles from KC
	 */
	const fetchClientRoles = () => {
		const token = loggedInUser.token;
		const clientRolesReq = getClientRoles(token);

		clientRolesReq.then((res) => {
			const responses = res.data.error == null ? res.data : [];

			const filteredRoles = responses.filter((res) =>
				validateRolesNames(res.name)
			);

			const formattedRoles = filteredRoles.map((role) => ({
				name: role.name,
				id: role.id,
			}));

			setKCRoles(formattedRoles);
			// setKCRoles(filteredRoles);
		});
	};

	/**
	 * Validate and filter valid roles
	 * @param {string} name
	 * @returns Valid roles array
	 */
	const validateRolesNames = (name) => {
		const invoiceReportRoles = [
			"administration:InvoiceTable:_viewReportBtn",
			"administration:InvoiceTable:_viewInvoiceBtn",
		];

		const validRoles =
			name.includes("_GY:") ||
			name.includes("_CLIENT:") ||
			(/^_[A-Z]{2}:GY/).test(name) || // for `${countryCode}:GY:${dept}`
			invoiceReportRoles.includes(name);

		return validRoles;
	};

	/**
	 * Handle users
	 * @param {array} users
	 */
	const handleUsers = (users) => {
		const formattedUsers = users.map((user) => {
			let site_desc = "";
			let company = "";

			const userIsGYUser = user.type == 1;
			const dept_desc = user.dept_desc ?? "-";
			const last_connection =
				user.last_connection === null
					? "Never"
					: moment(user.last_connection).format("DD MMM YYYY HH:mm");

			// GY Employee users
			if (userIsGYUser) {
				site_desc = "-";
				company = "GY";
			}
			// Client users
			else {
				// Sees all sites
				if (user.site_id == "all" || user.is_solo == 1) site_desc = "All Sites";
				// Sees multiple/single site
				else if (user.site_id != "all" && user.is_solo == 0) {
					const siteIDSplit = user.site_id.split(",");
					const noOfSites = siteIDSplit.length;

					// User has multiple site IDs
					if (noOfSites > 1) site_desc = "Multiple Sites";
					// User has one site ID
					else site_desc = "Single Sites";
				}

				company = user.company;
			}

			return { ...user, dept_desc, site_desc, company, last_connection };
		});

		// Filter users with multi sites
		const multiSiteUsers = users.filter(
			(user) => user.site_id != "all" && user.is_solo == 0
		);
		// Get site ID of multi sites
		const siteIDs = multiSiteUsers.map((user) => user.site_id);
		const joinedSiteID = siteIDs.join(",");

		// Fetch data of multi sites of all users
		getMultiSitesData(joinedSiteID);
		setKCUsers(formattedUsers);
	};

	/**
	 * Handle labels of single sites in table
	 * @param {obj} users
	 * @param {array} multiSites
	 */
	const handleSingleSites = (users, multiSites) => {
		const formattedUsers = users.map((user) => {
			if (user.site_desc === "Single Sites") {
				const siteID = user.site_id;
				const site = multiSites.find((site) => site.site_id == siteID);

				if (typeof site !== "undefined") user.site_desc = site.site_name;
			}

			return user;
		});

		setKCUsers(formattedUsers);
	};

	/**
	 * Fetch data of multi sites
	 * @param {string} siteIDs
	 */
	const getMultiSitesData = (siteIDs) => {
		const siteURI = `/site/filter/site_id/${siteIDs}`;

		GYAxios.get(siteURI).then((res) => {
			const sites = !res.data.error ? res.data : [];

			setMultiSites(sites);
		});
	};

	/**
	 * Get multi sites of user
	 * @param {obj} rowData
	 * @returns multi sites of user
	 */
	const getMultiSites = (rowData) => {
		const siteIDs = rowData.site_id;
		const siteIDSplit = siteIDs.split(",");
		const noOfSites = siteIDSplit.length;
		const hasMultiSites = siteIDs != "all" && noOfSites > 1;
		const isMultiSiteUser = rowData.is_solo == 0 && hasMultiSites;

		// User is a multi/single site user
		if (isMultiSiteUser) {
			// Map sites to users respectively
			const multiSitesArray = getSites(rowData.site_id, multiSites);
			const detailsPanelStyle = {
				width: "260px",
				marginLeft: "600px",
			};

			return (
				<ul style={detailsPanelStyle}>
					{multiSitesArray.map(getMultiSiteRow)}
				</ul>
			);
		}
	};

	/**
	 * Get multi site rows
	 * @param {obj} site
	 * @returns Multi site row
	 */
	const getMultiSiteRow = (site) => {
		return (
			<li key={site.site_id}>
				<b>{site.site_name}</b>
			</li>
		);
	};

	/**
	 * Match users sites to multi site fetched
	 * @param {string} siteIDs
	 * @param {array} multiSites
	 * @returns site IDs from fetch multi sites
	 */
	const getSites = (siteIDs, multiSites) => {
		const siteIDArray = siteIDs.split(",");
		const siteArray = siteIDArray.map((siteID) => {
			const site = multiSites.find((site) => site.site_id == siteID);
			return site;
		});

		return siteArray;
	};

	/**
	 * Update data of user
	 * @param {string} newValue
	 * @param {string} oldValue
	 * @param {string} field
	 * @param {obj} rowData
	 * @param {function} resolve
	 * @param {function} reject
	 */
	const updateData = (newValue, oldValue, field, rowData, resolve, reject) => {
		const trimmedNewValue = newValue.trim();
		const userID = rowData.user_id;

		// Value changed
		if (oldValue !== trimmedNewValue) {
			const loggedInUserID = loggedInUser.preferred_username;
			const now = moment().format("YYYY-MM-DD HH:mm:ss");

			// Build update parameter
			const updateParam = {};
			updateParam[field] = trimmedNewValue;
			updateParam["exported_status"] = 0;
			updateParam["last_updated_by"] = loggedInUserID;
			updateParam["last_updated"] = now;

			// Update URI
			const usersURI = `/users/${userID}`;

			// Promise
			GYAxios.patch(usersURI, updateParam)
				.then((res) => {
					// Update successfully
					if (res.data.affectedRows == 1 && res.data.serverStatus == 2) {
						Noti.create({
							type: "s",
							msg: `Updating ${field}....`,
						});
						resolve();
						setReload(true);
					}
					// Not updated
					else {
						Noti.create({
							type: "e",
							msg: "Update Error! Please contact IT",
						});
						reject();
					}
				})
				.catch((error) => {
					console.log("error UPDATE:", error);

					Noti.create({
						type: "e",
						msg: "Update Error! Please contact IT",
					});
					reject();
				});
		}
		// Value not changed
		else {
			Noti.create({
				type: "w",
				msg: "No Change!",
			});
			reject();
		}
	};

	/**
	 * Delete selected users
	 * @param {array} users
	 */
	const deleteUser = useCallback((users) => {
		// Delete Comfirmation dialog
		const confirmDelete = window.confirm(`Confirm delete users?`);

		if (confirmDelete) {
			const token = loggedInUser.token;

			// Prepare delete requests
			const deleteIDs = users.map((user) => user.user_id);
			const deleteRequests = deleteIDs.map((id) => getDeleteReq(id));

			// Prepare delete users from Keycloak API
			const deleteKeycloakIDs = users.map((user) => user.keycloak_id);
			const filteredKCIDs = deleteKeycloakIDs.filter((ID) => ID != null);
			const deleteRequestsKC = filteredKCIDs.map((id) =>
				deleteUsersKeycloak(token, id)
			);

			// Make delete requests
			Axios.all(deleteRequests).then(
				Axios.spread((...responses) => {
					const deleteErrors = responses.filter(
						(res) => res.data.affectedRows !== 1 && res.data.serverStatus != 2
					);

					if (deleteErrors.length > 0) {
						Noti.create({
							type: "e",
							msg: "Delete Error! Please contact IT",
						});
					} else {
						Noti.create({
							type: "s",
							msg: "Users Deleted Successfully",
						});
						setReload(true);
					}
				})
			);

			// Make delete requests to delete users from Keycloak
			Axios.all(deleteRequestsKC).then(
				Axios.spread((...responses) => {
					const deleteErrorsKC = responses.filter((res) => res.status !== 204);

					if (deleteErrorsKC.length > 0) {
						Noti.create({
							type: "e",
							msg: "Delete From Keycloak Error! Please contact IT",
						});
					}
				})
			);
		}
}, [ loggedInUser, setReload ]);

	/**
	 * Prepare API requests to assign roles to users
	 * @param {obj} user
	 * @returns
	 */
	const getRolesAssignReq = useCallback((user, keycloak_id) => {
		const token = loggedInUser.token;
		// Roles
		const roleNames = user.clientRoles.gymonitoring;
		const rolesArr = KCRoles.filter((KCRole) =>
			roleNames.includes(KCRole.name)
		);

		const assignRolesReq = setRolesToUsers(token, keycloak_id, rolesArr);
		return assignRolesReq;
	}, [KCRoles, loggedInUser]);

	const getKeycloakIdFromRes = useCallback(res => {
		// from create new user
		if (res.headers.location) {
			const KCID = extractKCIDFromHeadersLocation(res.headers.location);
			return KCID;
		}
		// from update existing user
		const KCID = extractKCIDFromReqUrl(res.config.url);
		return KCID;
	}, []);

	/**
	 * Execute role assignment to users
	 * @param {array} req
	 */
	const executeRolesAssign = async (req) => {
		return Axios.all(req)
			.then(
				Axios.spread((...responses) => {
					// console.log("responses roles assigned:", responses);
				})
			)
			.catch((err) => {
				console.log("err role assignment :", err);

				Noti.create({
					type: "e",
					msg: "Roles assignment Error! Please contact IT",
				});
			});
	};
	/**
	 * Update exported status of users
	 * @param {array} users
	 */
	const updateUserData = useCallback(async (userObjs) => {
		// Get update requests
		const updateRequests = userObjs.map((userObj) => getUpdateReq(userObj));

		// Make update requests
		return Axios.all(updateRequests).then(
			Axios.spread((...responses) => {
				const updateErrors = responses.filter(
					(res) => res.data.affectedRows != 1 && res.data.serverStatus != 2
				);

				// Update requests failed
				if (updateErrors.length > 0) {
					console.log("updateErrors :", updateErrors);

					Noti.create({
						type: "e",
						msg: "Update Error! Please contact IT",
					});
				} else setReload(true);
			})
		);
	}, [setReload]);

	/**
	 * Format user object into Keycloak structure
	 * @param {obj} users
	 * @returns formatted user object
	 */
	const formatNewUsers = useCallback((users) => {
		const formattedUsers = users.map((user) => {
			const country = user.country;
			const company = user.company;
			const dept = user.dept_desc;
			const siteID = user.site_id;

			const password = getPassword(country);
			const isSolo = user.is_solo == 1;

			const isGYUser = company === "GY";
			const userTypeRole = isGYUser ? "_GY" : "_CLIENT";
			const countryRole = `${userTypeRole}:${country}`;

			// Users' group
			const groups = isGYUser
				? [`/solarmonitoringGY`]
				: [`/solarmonitoringClients`];

			// Main role
			const mainRole = isGYUser
				? handleGYMainRole(userTypeRole, dept)
				: handleClientMainRole(userTypeRole, siteID, isSolo);

			const seesInvoice = user.see_invoice == 1;
			const seesReport = user.see_report == 1;
			const isExternal = user.is_external == 1;
			const rolesArray = [];

			// Add see invoice button role
			if (seesInvoice)
				rolesArray.push("administration:InvoiceTable:_viewInvoiceBtn");

			// Add see report button role
			if (seesReport)
				rolesArray.push("administration:InvoiceTable:_viewReportBtn");

			// Add external user role
			if (isExternal)
				rolesArray.push("_GY:External");

			// if the user is Thai O&M, add another role `_TH:GY:O&M`
			// according to this discussion: 
			if(isGYUser && dept === 'O&M' && country === 'TH') {
				rolesArray.push('_TH:GY:O&M');
			}

			// Add country and main roles to user
			rolesArray.push(countryRole);
			rolesArray.push(mainRole);

			return {
				username: user.username,
				email: user.email,
				firstName: user.first_name,
				lastName: user.last_name,
				enabled: true,
				emailVerified: true,
				requiredActions: ["UPDATE_PASSWORD"],
				credentials: [
					{
						type: "password",
						value: password,
						temporary: true,
					},
				],
				clientRoles: { gymonitoring: rolesArray },
				groups,
			};
		});

		return formattedUsers;
	}, []);

	const formatExistingUsers = useCallback((users) => {
		const formattedUsers = users.map((user) => {
			const country = user.country;
			const company = user.company;
			const dept = user.dept_desc;
			const siteID = user.site_id;

			const isSolo = user.is_solo == 1;

			const isGYUser = company === "GY";
			const userTypeRole = isGYUser ? "_GY" : "_CLIENT";
			const countryRole = `${userTypeRole}:${country}`;

			// Users' group
			const groups = isGYUser
				? [`/solarmonitoringGY`]
				: [`/solarmonitoringClients`];

			// Main role
			const mainRole = isGYUser
				? handleGYMainRole(userTypeRole, dept)
				: handleClientMainRole(userTypeRole, siteID, isSolo);

			const seesInvoice = user.see_invoice == 1;
			const seesReport = user.see_report == 1;
			const isExternal = user.is_external == 1;
			const rolesArray = [];

			// Add see invoice button role
			if (seesInvoice)
				rolesArray.push("administration:InvoiceTable:_viewInvoiceBtn");

			// Add see report button role
			if (seesReport)
				rolesArray.push("administration:InvoiceTable:_viewReportBtn");

			// Add external user role
			if (isExternal)
				rolesArray.push("_GY:External");

			// if the user is Thai O&M, add another role `_TH:GY:O&M`
			// according to this discussion: 
			if(isGYUser && dept === 'O&M' && country === 'TH') {
				rolesArray.push('_TH:GY:O&M');
			}

			// Add country and main roles to user
			rolesArray.push(countryRole);
			rolesArray.push(mainRole);

			return {
				keycloakUserId: user.keycloak_id,
				user: {
					firstName: user.first_name,
					lastName: user.last_name,
					clientRoles: { gymonitoring: rolesArray },
					groups,
				}
			};
		});

		return formattedUsers;
	}, [])

	/**
	 * Build multi line email list
	 * @param {array} emails
	 * @returns Email list string
	 */
	const buildMultiLineAlert = (emails) => {
		const listedEmails = emails.map((email) => `- ${email}`);
		const joinedMsg = listedEmails.join("\n");

		return joinedMsg;
	};

	/**
	 * Get Delete user promise
	 * @param {string} userID
	 * @returns Delete user Promise
	 */
	const getDeleteReq = (userID) => {
		// Delete URI
		const deleteURI = `/users/${userID}`;
		// Delete Request
		const deleteReq = GYAxios.delete(deleteURI);

		return deleteReq;
	};

	/**
	 * Extract KC ID from user's location string
	 * @param {string} location
	 * @returns KC ID of user
	 */
	const extractKCIDFromHeadersLocation = (location) => {
		const headerLocationSplit = location.split("/");
		const splitSize = headerLocationSplit.length;
		const KCid = headerLocationSplit[splitSize - 1];

		return KCid;
	};

	const extractKCIDFromReqUrl = url => {
		const KCID = /[a-z0-9\-]+$/g.exec(url)?.[0];
		return KCID;
	}

	/**
	 * Get Update user promise
	 * @param {string} userID
	 * @returns Update user promise
	 */
	const getUpdateReq = (userObj) => {
		const userID = userObj.user.user_id;
		const KCid = userObj.keycloak_id;

		// Update URI
		const usersURI = `/users/${userID}`;
		// Build update parameter
		const updateParam = { keycloak_id: KCid, exported_status: true };

		// Update Request
		const updateReq = GYAxios.patch(usersURI, updateParam);

		return updateReq;
	};

	/**
	 * Get GY User main role
	 * @param {string} userTypeRole
	 * @param {string} dept
	 * @returns GY User main role
	 */
	const handleGYMainRole = (userTypeRole, dept) => `${userTypeRole}:${dept}`;

	const activateUsers = useCallback(async users => {
		Promise.all(users.map(user => {
			return Promise.resolve()
				.then(() => activateKeycloakUser(loggedInUser.token, user.keycloak_id))
				.then(() => GYAxios.patch(`/users/${user.user_id}`, { activated_status: 1 }));
		})).then(() => {
			Noti.create({ type: 's', msg: 'All selected users are activated' })
		}).catch(err => {
			Noti.create({ type: 'e', msg: 'Some or all users cannot be activated!' })
		}).then(() => {
			setReload(true);
		});
		
	}, [loggedInUser, setReload]);

	const deactivateUsers = useCallback(async users => {
		Promise.all(users.map(user => {
			return Promise.resolve()
				.then(() => deactivateKeycloakUser(loggedInUser.token, user.keycloak_id))
				.then(() => GYAxios.patch(`/users/${user.user_id}`, { activated_status: 0 }));
		})).then(() => {
			Noti.create({ type: 's', msg: 'All selected users are de-activated' })
		}).catch(err => {
			Noti.create({ type: 'e', msg: 'Some or all users cannot be activated!' })
		}).then(() => {
			setReload(true);
		});
		
	}, [loggedInUser, setReload]);

	/**
	 * Get clients' main role
	 * @param {string} userTypeRole
	 * @param {string} siteID
	 * @returns Clients' main role
	 */
	const handleClientMainRole = (userTypeRole, siteID, isSolo) => {
		const seesAllSites = siteID === "all" && !isSolo;
		const siteIDsArr = siteID.split(",");
		const hasMultiSites = siteIDsArr.length > 1 && !isSolo;

		const multiSiteRole =
			seesAllSites || hasMultiSites
				? `${userTypeRole}:Multi`
				: `${userTypeRole}:Base`;

		return multiSiteRole;
	};

	/**
	 * sen demail to PM / asset manager by looking at last_updated_by column
	 * @param {array} exportedUserObjs
	 */
	const sendEmailToUpdatedByAndNewlyCreatedGYUsers = async exportedUserObjs => {
		// group users by user_accounts.client_id (company), user_accounts.country, user_accounts.last_updated_by, and operationType
		// then loop through each group to send email to last_updated_by (PM / asset manager)
		const userObjGroups = [];
		const uniqueueClientIds = [...new Set(exportedUserObjs.map(userObj => userObj.user.client_id))];
		const uniqueueCountries = [...new Set(exportedUserObjs.map(userObj => userObj.user.country))];
		const uniqueueUpdatedBy = [...new Set(exportedUserObjs.map(userObj => userObj.user.last_updated_by))];
		const uniqueueOperationTypes = [...new Set(exportedUserObjs.map(userObj => userObj.operationType))];
		uniqueueClientIds.forEach(clientId => {
			uniqueueCountries.forEach(country => {
				uniqueueUpdatedBy.forEach(updatedBy => {
					uniqueueOperationTypes.forEach(operationType => {
						const userObjsInGroup = exportedUserObjs.filter(userObj => userObj.user.client_id === clientId && userObj.user.country === country && userObj.user.last_updated_by === updatedBy && userObj.operationType === operationType);
						if (userObjsInGroup.length) { userObjGroups.push(userObjsInGroup) }
					});
				});
			});
		});

		// get recipients (PM / asset manager)
		let recipients;
		try {
			const updatedByData = await Promise.all(uniqueueUpdatedBy.map(updatedBy => GYAxios.get(`/users/filter?column=username&value=${updatedBy}`).then(res => res.data)));
			recipients = updatedByData
				.filter(data => !data.error && data[0])
				.map(data => data[0])
				.map(updatedBy => ({ name: updatedBy.first_name, username: updatedBy.username, email: updatedBy.email }));
		} catch (err) {
			Noti.create({
				type: "e",
				msg: "Error occured! Please contact IT",
				title: "Cannot fetch users to send notification emails"
			});
			console.error(err);
			return;
		}

		// // Send emails to the user performing action per client ID
		const sendEmailPromises = userObjGroups.map((userObjs) => {
			const operationType = userObjs[0].operationType;
			const company = userObjs[0].user.company || "Administraion";
			const country = userObjs[0].user.country;
			const lastUpdatedBy = userObjs[0].user.last_updated_by;
			// send email to PM & and asset manage
			let sendGroupedEmailPromises = [];
			if(lastUpdatedBy) { // last_updated_by can be NULL
				const recipient = recipients.find(recipient => recipient.username === lastUpdatedBy);
				if(recipient) { // if the user last_updated_by is deleteed from DB, the recipient is undefined
					sendGroupedEmailPromises.push(sendUserExportedEmail(company, country, recipient, userObjs.map(userObj => userObj.user), operationType))
				}
			}
			// if newly created users are GY users, also send email to them directly
			if(operationType === 'NEW') {
				userObjs.filter(userObj => typeof(userObj.user.dept_id) === 'number').forEach(userObj => {
					const recipient = { name: userObj.user.first_name, username: userObj.user.username, email: userObj.user.email };
					sendGroupedEmailPromises.push(sendUserExportedEmail(company, country, recipient, [userObj.user], operationType));
				});
			}
			return Promise.all(sendGroupedEmailPromises);
		});
		return Promise.all(sendEmailPromises);
	};

	/**
	 * Export users to JSON file
	 * @param {array} users
	 */
	const exportUsersToKeycloak = useCallback((users) => {
		const newUsers = users.filter(user => !user.keycloak_id);
		const existingUsers = users.filter(user => !!user.keycloak_id);
		const formattedNewUsers = formatNewUsers(newUsers);
		const formattedExistingUsers = formatExistingUsers(existingUsers);

		const token = loggedInUser.token;

		// Get request to add users to keycloak
		const exportNewUsersReq = formattedNewUsers.map((user, index) => ({ type: 'NEW', req: addUsersToKeycloak(token, user), user: newUsers[index] }));
		const exportExistingUsersReq = formattedExistingUsers.map((obj, index) => ({ type: 'UPDATE', req: updateUsersToKeycloak(token, obj.user, obj.keycloakUserId), user: existingUsers[index] }));

		const reqObjs = [...exportNewUsersReq, ...exportExistingUsersReq];

		// there are requestes to be handled
		Axios.all(reqObjs.map(reqObj => reqObj.req))
			.then(
				Axios.spread(async (...responses) => {
					const exportErrors = responses.filter(res => !(res.status === 201 || res.status === 204));
					// Export requests failed
					if (exportErrors.length > 0) {
						Noti.create({
							type: "e",
							msg: "Export Error! Please contact IT",
						});
					}

					// Re-map users' data with Keycloak ID
					// use Object.entries(), as we want to refer to `reqObjs`
					const exportedUserObjs = Object.entries(responses)
						.filter((resEntry) => (resEntry[1].status === 201 || resEntry[1].status === 204))
						.map(resEntry => ({
							user: reqObjs[parseInt(resEntry[0])].user,
							operationType: reqObjs[parseInt(resEntry[0])].type,
							keycloak_id: getKeycloakIdFromRes(resEntry[1]),
							reqBody: JSON.parse(resEntry[1].config.data)
						}));

					// Assign Roles
					const assignedUsersReq = exportedUserObjs.map((userObj) => getRolesAssignReq(userObj.reqBody, userObj.keycloak_id));
					await executeRolesAssign(assignedUsersReq);

					// Update user data in DB
					await updateUserData(exportedUserObjs);

					if (exportedUserObjs.length > 0) {
						const finding_new_users = exportedUserObjs.filter(userObj => userObj.operationType == 'NEW');
						const finding_recipients = finding_new_users.filter(userObj => userObj.user.receiving_einvoice === 1);
						const recipients = finding_recipients.map(userObj => {
							return {
								email: userObj.user.email,
								client_id: userObj.user.client_id,
								site_id: userObj.user.site_id,
							}
						});

						await GYAxios.post('/users/new_einvoice_recipients/', { recipients });
					}

          // Send email to updated_by and newly created GY users
					await sendEmailToUpdatedByAndNewlyCreatedGYUsers(exportedUserObjs);
					// send email to IT to notify that users are exported to keycloak
					await sendUserExportedNotificationToIT(exportedUserObjs.map(userObj => userObj.user), loggedInUser);

					// if everything is successful, then show message notification
					const userEmails = exportedUserObjs.map(userObj => userObj.user.email).join(", ");
					Noti.create({
						type: "s",
						msg: `Users Exported Successfully!`,
						title: userEmails
					});
					setReload(true);
				})
			)
			.catch((err) => {
				console.log("err :", err);
				Noti.create({
					type: "e",
					msg: "Error occured! Please contact IT",
					title: err.response?.data?.errorMessage || err.response?.data?.error || null
				});
			});
	}, [formatExistingUsers, formatNewUsers, getKeycloakIdFromRes, getRolesAssignReq, loggedInUser, setReload, updateUserData]);

	const handleEmailNotificationModelOK = useCallback(async () => {
		if(
			!usersToSendEmailNoti?.length ||
			!emailNotiBody.trim() ||
			!emailNotiSubject.trim()
		) { return setIsEmailNotificationModelOpen(false) }
		const sentEmails = [];
		for(let user of usersToSendEmailNoti) {
			const payload = {
				subject: emailNotiSubject.trim(),
				recipient: { email: user.email, name: user.first_name },
				emailBody: emailNotiBody.trim()
			};
			try {
				await GYAxios.post('/users/send_email/custom', payload);
				sentEmails.push(user.email);
			} catch(err) {
				Noti.create({ type: 'e', msg: 'Cannot send email. Please check user\'s email and name and try again', title: user.email });
			}
		}
		if(sentEmails.length) {
			Noti.create({ type: 's', msg: 'Sent email notifications', title: sentEmails.join(', ') });
		}
		setUsersToSendEmailNoti(null);
		setIsEmailNotificationModelOpen(false);
	}, [emailNotiBody, emailNotiSubject, usersToSendEmailNoti]);

	/**
	 * Validate and export selected users to JSON file
	 * @param {array} users
	 */
	const prepareExportUsers = useCallback((users) => {
		const confirmMsg = "Confirm export these users to Keycloak?";
		// Display confirmation dislog
		const confirmExport = window.confirm(confirmMsg);

		// Confirmed to export users
		if (confirmExport) {
			// Export users to JSON of Keycloak format
			exportUsersToKeycloak(users);
		}
	}, [ exportUsersToKeycloak ]);

	useEffect(() => {
		fetchClientRoles();
	}, []);

	useEffect(() => {
		handleUsers(props.KCUsers);
	}, [props.KCUsers]);

	useEffect(() => {
		if (multiSites.length > 0) handleSingleSites(KCUsers, multiSites);
	}, [multiSites]);

	useEffect(() => {
		if (Array.isArray(KCUsers)) {
			if (KCUsers.length > 0) {
				dividePages(KCUsers.length, 30) === null
					? Noti.create({
						type: "e",
						msg: "Parameter for dividing pages is mismatch!",
					})
					: setNoOfPages(dividePages(KCUsers.length, 30));
				setTableState(true);
			}
		}

	}, [KCUsers]);

	const tableButtons = useMemo(() => {
		const buttons = [
			{
				icon: () => <span className="glyphicon glyphicon-thumbs-up"></span>,
				tooltip: "Activate users",
				onClick: (evt, users) => { activateUsers(users) },
			},
			{
				icon: () => <span className="glyphicon glyphicon-thumbs-down"></span>,
				tooltip: "Deactivate users",
				onClick: (evt, users) => { deactivateUsers(users) },
			},
			{
				icon: () => <span className="glyphicon glyphicon-bullhorn"></span>,
				tooltip: "Send email notification to users",
				onClick: (evt, users) => { setUsersToSendEmailNoti(users); setIsEmailNotificationModelOpen(true) },
			},
			{
				icon: 'edit',
				tooltip: 'Edit selected users',
				onClick: (event, users) => {
					if(users.some(user => user.type !== 1)) {
						return Noti.create({ type: 'w', msg: 'You can only edit GY users. Please check your selected users' });
					}
					setSelectedKeycloakUsers(users);
					router.push("/solar/administration/users/GY/form")
				}
			},
			// {
			// 	icon: "delete",
			// 	tooltip: "Remove All Selected Users",
			// 	onClick: (evt, data) => deleteUser(data),
			// },
			{
				icon: () => <span className="glyphicon glyphicon-share"></span>,
				tooltip: "Export users to Keycloak",
				onClick: (evt, data) => prepareExportUsers(data),
			},
			{
				icon: "add",
				tooltip: "Add users",
				isFreeAction: true,
				onClick: (event, data) => {
					setSelectedKeycloakUsers(null);
					router.push("/solar/administration/users/GY/form");
				},
			},
		];
		return buttons;
	}, [activateUsers, deactivateUsers, setSelectedKeycloakUsers, router, deleteUser, prepareExportUsers]);

	return (
		<div>
			<MuiThemeProvider theme={theme}>
				{tableState && (
					<MaterialTable
						columns={initColumns}
						data={KCUsers}
						isLoading={props.reload}
						options={{
							columnsButton: true,
							showTitle: false,
							grouping: true,
							selection: true,
							selectionProps: (rowData) => ({
								color: "primary",
							}),
							pageSize: noOfPages[0]?.value ? noOfPages[0].value : noOfPages[0],
							pageSizeOptions: noOfPages,
							headerStyle: {
								fontSize: "1rem",
								textAlign: "center",
							},
							fixedColumns: {
								right: 2,
							},
							emptyRowsWhenPaging: false,
							exportButton: {
								csv: true,
								pdf: false,
							},
							exportFileName: `KeyCloak User Data - updated on ${moment().format(
								"YYYYMMDD HHmmss"
							)}`,
							exportAllData: true,
						}}
						detailPanel={[
							{
								icon: () => <span></span>,
								render: (rowData) => getMultiSites(rowData),
							},
						]}
						cellEditable={{
							onCellEditApproved: (newValue, oldValue, rowData, columnDef) => {
								const field = columnDef.field;

								return new Promise((resolve, reject) => {
									updateData(
										newValue,
										oldValue,
										field,
										rowData,
										resolve,
										reject
									);
								});
							},
						}}
						actions={tableButtons}
						onRowClick={(event, rowData, togglePanel) => togglePanel()}
					/>
				)}
			</MuiThemeProvider>
			<Noti />
			<Modal title="Notify content to be sent as email" open={isEmailNotificationModelOpen} onOk={handleEmailNotificationModelOK} onCancel={() => setIsEmailNotificationModelOpen(false)}>
				<Form layout="vertical">
					<Form.Item label="Email Subject">
							<Input value={emailNotiSubject} onChange={e => setEmailNotiSubject(e.target.value)} />
					</Form.Item>
					<Form.Item label="Email Body HTML">
						<TextArea value={emailNotiBody} onChange={e => setEmailNotiBody(e.target.value)}></TextArea>
					</Form.Item>
				</Form>
      </Modal>
		</div>
	);
};

export default KeycloakPanelTable;
